import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import { useCategories } from './CategoriesContext';
import { productService } from '../../shared/utils/serviceProvider';
import { logAuditEvent, AuditAction, AuditLevel } from '../../shared/utils/auditLogger';
import i18n from '../../i18n';

const pr = (key, opts) => i18n.t(`serviceMessages.products.${key}`, opts);

export const ProductsContext = createContext();

// Custom hook for using the ProductsContext
export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error(pr('hook_outside_provider'));
  }
  return context;
};

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Refresh products function (can be called externally)
  const refreshData = useCallback(async () => {
    // Create AbortController to handle request cancellation
    const abortController = new AbortController();
    
    setLoading(true);
    try {
      const productsData = await productService.getProducts({ signal: abortController.signal });
      if (productsData && Array.isArray(productsData)) {
        setProducts(productsData);
      } else {
        setProducts([]);
      }
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError') {
        console.log('Product refresh was cancelled');
        return;
      }
      console.error('Error refreshing products from database:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
    
    // Return cleanup function
    return () => {
      abortController.abort();
    };
  }, []);

  // Load products from database on mount
  useEffect(() => {
    // Create AbortController for this effect
    const abortController = new AbortController();
    
    const loadProducts = async () => {
      try {
        const productsData = await productService.getProducts({ signal: abortController.signal });
        if (productsData && Array.isArray(productsData)) {
          setProducts(productsData);
        } else {
          setProducts([]);
        }
      } catch (error) {
        // Ignore abort errors
        if (error.name === 'AbortError') {
          console.log('Product loading was cancelled');
          return;
        }
        console.error('Error loading products from database:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
    
    // Cleanup function to abort request if component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  // Listen for refresh events from sidebar and stock changes
  useEffect(() => {
    const handleRefresh = () => {
      console.log('Products context received refresh event');
      refreshData();
    };

    const handleDataChanged = () => {
      console.log('📦 Products context: DATA_CHANGED event received - refreshing products');
      refreshData();
    };

    // Listen for general refresh events
    window.addEventListener('refresh-products', handleRefresh);
    window.addEventListener('refresh-all-data', handleRefresh);
    window.addEventListener('refresh-section', (e) => {
      if (e.detail?.section === 'products') {
        handleRefresh();
      }
    });

    // Listen for stock changes and other data updates
    eventEmitter.on(EVENT_TYPES.DATA_CHANGED, handleDataChanged);

    return () => {
      window.removeEventListener('refresh-products', handleRefresh);
      window.removeEventListener('refresh-all-data', handleRefresh);
      window.removeEventListener('refresh-section', handleRefresh);
      eventEmitter.off(EVENT_TYPES.DATA_CHANGED, handleDataChanged);
    };
  }, [refreshData]);

  // We don't need to save products back to database on every change
  // as each operation will directly update the database

  // Add a new product
  const addProduct = useCallback(async (product) => {
    try {
      // Validate required fields
      if (!product.name || !product.name.trim()) {
        throw new Error(pr('name_required'));
      }
      
      // Convert frontend field names to API expected format if needed
      const apiProduct = {
        ...product,
        // Ensure all required fields are present
        name: product.name.trim(),
        description: product.description?.trim() || '',
        category: product.category?.trim() || '',
        type: product.type || 'product',
        status: product.status || 'active',
        unit: product.unit?.trim() || pr('default_unit'),
        reference: product.reference?.trim() || '',
        price: parseFloat(product.price) || 0,
        stock: product.type === 'service' ? null : parseInt(product.stock) || 0,
        // Add timestamps
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // DEBUG: Log what we're sending
      console.log('🔍 FRONTEND - Original product:', product);
      console.log('🔍 FRONTEND - product.reference:', product.reference);
      console.log('🔍 FRONTEND - Sending to API:', apiProduct);
      console.log('🔍 FRONTEND - apiProduct.reference:', apiProduct.reference);

      // Add to database
      const result = await productService.createProduct(apiProduct);
      
      if (result) {
        // Ensure the result has all necessary fields
        const newProduct = {
          ...apiProduct,
          ...result,
          id: result.id || Date.now() // Fallback ID if not provided
        };
        
        // Update local state after database operation
        setProducts(prev => {
          const updated = [...prev, newProduct];
          
          // Emit events for real-time updates
          eventEmitter.emit(EVENT_TYPES.PRODUCT_CREATED, newProduct);
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });

        // Log audit event
        logAuditEvent(
          'product_create',
          pr('audit_product', { name: newProduct.name }),
          pr('audit_create_detail', { ref: newProduct.reference || '' }),
          AuditLevel.SUCCESS
        );

        return newProduct;
      } else {
        throw new Error(pr('add_failed'));
      }
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  }, []);

  // Update an existing product
  const updateProduct = useCallback(async (id, updatedProduct) => {
    try {
      console.log('ProductsContext.updateProduct:', id, updatedProduct);
      
      const oldProduct = products.find(product => product.id === id);
      if (!oldProduct) {
        throw new Error(pr('not_found', { id }));
      }
      
      // Make sure all data types are correct for the API
      const apiUpdates = { 
        ...updatedProduct,
        reference: updatedProduct.reference?.trim() || '',
        price: parseFloat(updatedProduct.price || 0),
        stock: updatedProduct.type === 'service' ? null : parseInt(updatedProduct.stock || 0),
        active: updatedProduct.status === 'active' ? 1 : 0,
        updated_at: new Date().toISOString(),
      };
      
      console.log('Sending API update with data:', apiUpdates);
      
      const result = await productService.updateProduct(id, apiUpdates);
      console.log('API update result:', result);
      
      if (result) {
        // Use the result from API or create updated product object
        const productToUpdate = { 
          ...oldProduct, 
          ...updatedProduct, 
          id, 
          updated_at: new Date().toISOString(),
          // Ensure status and active are in sync
          status: updatedProduct.status || (updatedProduct.active ? 'active' : 'inactive'),
          active: updatedProduct.active || (updatedProduct.status === 'active' ? 1 : 0)
        };
        
        // Update local state after database operation
        setProducts(prev => {
          const updated = prev.map(product => product.id === id ? productToUpdate : product);
          
          console.log('Product updated in local state:', id, productToUpdate);
          
          // Check if price changed for specific event
          if (oldProduct.price !== updatedProduct.price) {
            eventEmitter.emit(EVENT_TYPES.PRODUCT_PRICE_CHANGED, {
              id,
              oldPrice: oldProduct.price,
              newPrice: updatedProduct.price
            });
          }
          
          // Emit general update event
          eventEmitter.emit(EVENT_TYPES.PRODUCT_UPDATED, {
            id,
            oldProduct,
            newProduct: productToUpdate
          });
          
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'product_update',
          pr('audit_product', { name: productToUpdate.name }),
          pr('audit_update_detail', { id }),
          AuditLevel.INFO
        );
        
        return productToUpdate;
      } else {
        throw new Error(pr('update_failed'));
      }
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }, [products]);

  // Delete a product
  const deleteProduct = useCallback(async (id) => {
    try {
      const productToDelete = products.find(product => product.id === id);
      if (!productToDelete) {
        throw new Error(pr('not_found', { id }));
      }
      
      // Delete from database
      const result = await productService.deleteProduct(id);
      
      if (result) {
        // Update local state after database operation
        setProducts(prev => {
          const updated = prev.filter(product => product.id !== id);
          
          console.log('Product deleted:', id);
          
          // Emit delete event
          eventEmitter.emit(EVENT_TYPES.PRODUCT_DELETED, productToDelete);
          eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
          
          return updated;
        });
        
        // Log audit event
        logAuditEvent(
          'product_delete',
          pr('audit_product', { name: productToDelete.name }),
          pr('audit_delete_detail', { id }),
          AuditLevel.WARNING
        );
      } else {
        throw new Error(pr('delete_failed'));
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }, [products]);

  // Get a product by ID
  const getProductById = useCallback((id) => {
    return products.find(product => product.id === id);
  }, [products]);

  // Get active products only
  const getActiveProducts = useCallback(() => {
    return products.filter(product => product.status !== 'inactive');
  }, [products]);

  // Filter products by various criteria
  const filterProducts = useCallback((filters) => {
    return products.filter(product => {
      let match = true;

      if (filters.category && product.category !== filters.category) {
        match = false;
      }

      if (filters.type && product.type !== filters.type) {
        match = false;
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const nameMatch = product.name && product.name.toLowerCase().includes(searchLower);
        const descMatch = product.description && product.description.toLowerCase().includes(searchLower);
        
        if (!(nameMatch || descMatch)) {
          match = false;
        }
      }

      return match;
    });
  }, [products]);

  // Value object to be provided to consumers
  const value = {
    products,
    loading,
    addProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    getActiveProducts,
    filterProducts,
    refreshData
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}; 

